import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollTo } from "@/lib/lenis";
import { NAV_LINKS, LOGO } from "@/lib/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollTo(`#${id}`);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-[80] transition-[padding,background-color] duration-500 ${scrolled ? "glass-panel py-2" : "bg-transparent py-5"}`}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between gap-4">
          <button onClick={() => go("home")} className="flex items-center gap-3 shrink-0" data-testid="nav-logo" aria-label="HA Treasure House home">
            <img
              src={LOGO}
              alt="HA Treasure House logo"
              className={`rounded-full object-cover ring-2 ring-gold/80 shadow-[0_0_18px_rgba(212,175,55,0.55)] transition-all duration-500 ${scrolled ? "h-14 w-14" : "h-20 w-20"}`}
            />
            <span className={`font-display font-semibold tracking-wide gold-text hidden sm:block transition-all duration-500 ${scrolled ? "text-sm" : "text-lg"}`}>
              HA TREASURE HOUSE
            </span>
          </button>

          <nav className="hidden xl:flex items-center gap-4" data-testid="nav-links-desktop">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                className="text-xs tracking-wide whitespace-nowrap text-cream/80 hover:text-gold transition-colors duration-300"
                data-testid={`nav-${l.id}-link`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => go("register")}
              className="hidden sm:inline-flex items-center rounded-full bg-gold px-6 py-2.5 text-sm font-semibold tracking-wider text-royal-950 transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_25px_rgba(212,175,55,0.5)]"
              data-testid="nav-register-cta"
            >
              REGISTER NOW
            </button>
            <button
              onClick={() => setOpen(true)}
              className="xl:hidden text-gold p-2"
              aria-label="Open menu"
              data-testid="nav-hamburger"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-royal-950/97 backdrop-blur-xl flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 py-5">
              <img src={LOGO} alt="HA Treasure House logo" className="h-14 w-14 rounded-full ring-2 ring-gold/80 shadow-[0_0_18px_rgba(212,175,55,0.55)] object-cover" />
              <button onClick={() => setOpen(false)} className="text-gold p-2" aria-label="Close menu" data-testid="mobile-menu-close">
                <X size={28} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col items-center justify-center gap-5 px-6">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.5 }}
                  onClick={() => go(l.id)}
                  className="font-display text-2xl text-cream/90 hover:text-gold transition-colors"
                  data-testid={`mobile-nav-${l.id}-link`}
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                onClick={() => go("register")}
                className="mt-4 rounded-full bg-gold text-royal-950 px-8 py-3 font-semibold tracking-wider transition-all duration-300 hover:bg-gold-light"
                data-testid="mobile-nav-register-cta"
              >
                REGISTER NOW
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
